import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { format } from 'date-fns';


function ReviewModal({ lesson, onClose, onSubmit }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/reviews', { lesson_id: lesson.id, tutor_id: lesson.tutor_id, rating, review_text: text });
      onSubmit();
    } catch(e) {}
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
        <button className="modal-close" onClick={onClose}>X</button>
        <h2 className="modal-title">Leave a review</h2>
        <p style={{fontSize:'0.875rem',color:'var(--ink-400)',marginBottom:20}}>How was your lesson with {lesson.tutor_name}?</p>
        <div style={{display:'flex',gap:8,marginBottom:20,justifyContent:'center'}}>
          {[1,2,3,4,5].map(n=>(
            <button key={n} type="button" onClick={()=>setRating(n)} style={{
              fontSize:'2rem',background:'none',border:'none',cursor:'pointer',
              color: n<=rating ? '#f59e0b' : '#ddd', transition:'color 0.15s'
            }}>&#9733;</button>
          ))}
        </div>
        <div className="form-group">
          <label className="form-label">Your review (optional)</label>
          <textarea className="form-textarea" rows={4} value={text} onChange={e=>setText(e.target.value)}
            placeholder="Tell other parents about this tutor..."/>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Skip</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Submitting...' : 'Submit review'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ParentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewLesson, setReviewLesson] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);

  useEffect(()=>{
    api.get('/parents/dashboard').then(r=>setData(r.data)).finally(()=>setLoading(false));
    api.get('/reviews/pending').then(r=>setPendingReviews(r.data.lessons||[])).catch(()=>{});
  },[]);

  if (loading) return <DashboardLayout title="Dashboard"><div className="loading-screen"><div className="spinner"/></div></DashboardLayout>;

  const parent = data?.parent || {};
  const wallet = user?.currency==='USD' ? parent.wallet_balance_usd : parent.wallet_balance_gbp;
  const sym = user?.currency==='USD' ? '$' : 'GBP';
  const fmtC = v => sym + parseFloat(v||0).toFixed(2);

  return (
    <DashboardLayout title="Dashboard" subtitle={'Welcome back, ' + user?.first_name}>
      <div className="stats-grid" style={{marginBottom:24}}>
        <div className="stat-card" style={{background:'linear-gradient(135deg,#edfaf3,#fff)',border:'1.5px solid var(--green-200)'}}>
          <div className="stat-card-label">Wallet balance</div>
          <div className="stat-card-value" style={{color:'var(--green-700)'}}>{fmtC(wallet)}</div>
          <Link to="/parent/wallet" style={{fontSize:'0.78rem',color:'var(--green-600)',fontWeight:700,marginTop:4,display:'block'}}>Top up</Link>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Upcoming lessons</div>
          <div className="stat-card-value">{data?.upcomingLessons?.length || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Awaiting sign-off</div>
          <div className="stat-card-value">{data?.pendingSignoff?.length || 0}</div>
        </div>
      </div>

      {pendingReviews.length > 0 && (
        <div className="card" style={{marginBottom:20,borderColor:'#f59e0b',background:'#fffbeb'}}>
          <h3 style={{marginBottom:12,fontSize:'1rem',color:'#92400e'}}>Leave a review</h3>
          {pendingReviews.map(l=>(
            <div key={l.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:'1px solid #fde68a'}}>
              <div>
                <div style={{fontWeight:600,fontSize:'0.875rem'}}>{l.subject_name} with {l.tutor_name}</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={()=>setReviewLesson(l)}>Leave review</button>
            </div>
          ))}
        </div>
      )}
      {data?.pendingSignoff?.length > 0 && (
        <div className="card" style={{marginBottom:20,borderColor:'var(--warning)',background:'#fffbeb'}}>
          <h3 style={{marginBottom:12,fontSize:'1rem',color:'#92400e'}}>Lessons awaiting sign-off</h3>
          {data.pendingSignoff.map(l=>(
            <div key={l.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:'1px solid #fde68a'}}>
              <div>
                <div style={{fontWeight:600,fontSize:'0.875rem'}}>{l.subject_name} with {l.tutor_name}</div>
                <div style={{fontSize:'0.78rem',color:'#92400e'}}>{format(new Date(l.scheduled_at),'EEE d MMM')}</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={()=>api.post('/lessons/'+l.id+'/signoff').then(()=>window.location.reload())}>
                Sign off
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <h3 style={{margin:0,fontSize:'1rem'}}>Upcoming lessons</h3>
            <Link to="/parent/bookings" style={{fontSize:'0.78rem',color:'var(--green-600)',fontWeight:700}}>View all</Link>
          </div>
          {!data?.upcomingLessons?.length
            ? <div style={{textAlign:'center',padding:'20px 0',color:'var(--ink-400)'}}>
                <p style={{margin:'0 0 12px',fontSize:'0.875rem'}}>No lessons booked yet.</p>
                <Link to="/find-tutors" className="btn btn-primary btn-sm">Find a tutor</Link>
              </div>
            : data.upcomingLessons.map(l=>(
              <div key={l.id} style={{padding:'10px 0',borderBottom:'1px solid var(--ink-50)',display:'flex',gap:12,alignItems:'center'}}>
                <div className="avatar-placeholder avatar-sm" style={{fontSize:'0.72rem'}}>{l.tutor_name?.split(' ').map(w=>w[0]).join('')}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:'0.875rem'}}>{l.tutor_name}</div>
                  <div style={{fontSize:'0.75rem',color:'var(--ink-400)'}}>{l.subject_name} - {format(new Date(l.scheduled_at),'EEE d MMM, HH:mm')}</div>
                </div>
                {l.meet_link && <a href={l.meet_link} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">Join</a>}
              </div>
            ))
          }
        </div>

        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <h3 style={{margin:0,fontSize:'1rem'}}>Recent bookings</h3>
            <Link to="/parent/bookings" style={{fontSize:'0.78rem',color:'var(--green-600)',fontWeight:700}}>View all</Link>
          </div>
          {!data?.recentBookings?.length
            ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No bookings yet.</p>
            : data.recentBookings.map(b=>(
              <div key={b.id} style={{padding:'10px 0',borderBottom:'1px solid var(--ink-50)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:600,fontSize:'0.875rem'}}>{b.tutor_name}</div>
                  <div style={{fontSize:'0.75rem',color:'var(--ink-400)'}}>{b.subject}</div>
                </div>
                <span className={'badge ' + (b.status==='confirmed'?'badge-green':b.status==='pending'?'badge-yellow':'badge-grey')}>{b.status}</span>
              </div>
            ))
          }
        </div>
      </div>
          {reviewLesson && (
        <ReviewModal lesson={reviewLesson} onClose={()=>setReviewLesson(null)}
          onSubmit={()=>{ setReviewLesson(null); setPendingReviews(pendingReviews.filter(l=>l.id!==reviewLesson.id)); }}/>
      )}
    </DashboardLayout>
  );
}

export function ParentWallet() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [topping, setTopping] = useState(false);
  const [topupSuccess, setTopupSuccess] = useState(null);
  const [error, setError] = useState('');

  const load = () => api.get('/payments/wallet').then(r=>setData(r.data)).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  const currency = user?.currency || 'GBP';
  const sym = currency==='USD' ? '$' : '£';
  const balance = currency==='USD' ? data?.wallet?.usd : data?.wallet?.gbp;
  const min = currency==='USD' ? 10 : 8;
  const presets = currency==='USD' ? [10,20,50,100] : [8,16,40,80];
  const txTypeLabel = { topup:'Top-up', booking_debit:'Lesson booked', refund:'Refund', admin_credit:'Credit', admin_debit:'Debit' };
  const txColor = { topup:'var(--success)', booking_debit:'var(--error)', refund:'var(--success)', admin_credit:'var(--success)', admin_debit:'var(--error)' };
  const txSign = { topup:'+', booking_debit:'-', refund:'+', admin_credit:'+', admin_debit:'-' };

  const handleTopup = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents < min * 100) return setError('Minimum top-up is ' + sym + min);
    setTopping(true); setError('');
    try {
      await api.post('/payments/topup/test', { amount: cents });
      setTopupSuccess(parseFloat(amount));
      setAmount('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
    setTopping(false);
  };

  return (
    <DashboardLayout title="Wallet">
      <div style={{display:'grid',gridTemplateColumns:'1fr 380px',gap:24,alignItems:'start'}}>
        <div>
          {topupSuccess && (
            <div className="alert alert-success" style={{marginBottom:16}}>
              Added {sym}{topupSuccess.toFixed(2)} to your wallet!
            </div>
          )}
          <div style={{background:'linear-gradient(135deg,#052e1c,#0d6e4f)',borderRadius:16,padding:'28px',marginBottom:20,color:'#fff'}}>
            <p style={{margin:'0 0 6px',fontSize:'0.82rem',color:'rgba(255,255,255,0.6)',fontWeight:600,textTransform:'uppercase'}}>Wallet balance</p>
            <div style={{fontSize:'3rem',fontWeight:800,lineHeight:1}}>{sym}{loading ? '...' : parseFloat(balance||0).toFixed(2)}</div>
            <p style={{margin:'8px 0 0',fontSize:'0.78rem',color:'rgba(255,255,255,0.45)'}}>Balance never expires - Lesson cost: {sym}{currency==='USD'?'10':'8'}/hr</p>
          </div>
          <div className="card">
            <h3 style={{marginBottom:16,fontSize:'1rem'}}>Transaction history</h3>
            {loading ? <div className="spinner"/> : !data?.transactions?.length
              ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No transactions yet.</p>
              : data.transactions.map((tx,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',borderBottom:'1px solid var(--ink-50)'}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:'0.875rem'}}>{txTypeLabel[tx.type]||tx.type}</div>
                    <div style={{fontSize:'0.75rem',color:'var(--ink-400)'}}>{format(new Date(tx.created_at),'d MMM yyyy, HH:mm')}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontWeight:800,color:txColor[tx.type]||'var(--ink)',fontSize:'1rem'}}>{txSign[tx.type]}{sym}{parseFloat(tx.amount).toFixed(2)}</div>
                    <div style={{fontSize:'0.72rem',color:'var(--ink-400)'}}>Balance: {sym}{parseFloat(tx.balance_after).toFixed(2)}</div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>

        <div className="card card-elevated" style={{position:'sticky',top:88}}>
          <h3 style={{marginBottom:4,fontSize:'1.1rem'}}>Top up wallet</h3>
          <p style={{fontSize:'0.8rem',color:'var(--ink-400)',marginBottom:20}}>Minimum: {sym}{min}</p>
          {error && <div className="alert alert-error" style={{marginBottom:16}}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Amount ({sym})</label>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:8}}>
              {presets.map(v=>(
                <button key={v} type="button" onClick={()=>setAmount(String(v))}
                  className={'btn btn-sm ' + (amount===String(v)?'btn-primary':'btn-ghost')}>{sym}{v}</button>
              ))}
            </div>
            <input type="number" className="form-input" value={amount} onChange={e=>setAmount(e.target.value)} min={min} step="1" placeholder={sym + min + '.00'}/>
          </div>
          <button className="btn btn-primary btn-full btn-lg" onClick={handleTopup} disabled={topping||!amount}>
            {topping ? 'Adding...' : 'Top up ' + sym + (amount||'0')}
          </button>
          <p style={{fontSize:'0.75rem',color:'var(--ink-400)',textAlign:'center',marginTop:10}}>Your balance never expires.</p>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function ParentBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(()=>{ api.get('/bookings/my').then(r=>setBookings(r.data.bookings||[])).finally(()=>setLoading(false)); },[]);

  const filtered = activeTab==='all' ? bookings : bookings.filter(b=>b.status===activeTab);
  const sym = user?.currency==='USD' ? '$' : 'GBP ';
  const statusColor = {confirmed:'badge-green',pending:'badge-yellow',cancelled:'badge-red',completed:'badge-grey',expired:'badge-grey'};

  return (
    <DashboardLayout title="My Bookings">
      <div style={{display:'flex',gap:4,marginBottom:20,borderBottom:'1.5px solid var(--ink-100)'}}>
        {['all','pending','confirmed','completed','cancelled'].map(tab=>(
          <button key={tab} onClick={()=>setActiveTab(tab)} style={{
            padding:'10px 14px',border:'none',background:'none',cursor:'pointer',fontWeight:700,fontSize:'0.82rem',
            color:activeTab===tab?'var(--green-700)':'var(--ink-400)',
            borderBottom:activeTab===tab?'2.5px solid var(--green-500)':'2.5px solid transparent',
            textTransform:'capitalize'
          }}>{tab}</button>
        ))}
        <div style={{marginLeft:'auto',padding:'8px 0'}}>
          <Link to="/find-tutors" className="btn btn-primary btn-sm">+ Book a tutor</Link>
        </div>
      </div>
      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : filtered.length===0
        ? <div style={{textAlign:'center',padding:'60px 0',color:'var(--ink-400)'}}>
            <p style={{marginBottom:16}}>No bookings yet.</p>
            <Link to="/find-tutors" className="btn btn-primary">Find a tutor</Link>
          </div>
        : <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {filtered.map(b=>(
            <div key={b.id} className="card" style={{padding:18}}>
              <div style={{display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
                <div className="avatar-placeholder avatar-md" style={{fontSize:'0.9rem'}}>{b.tutor_name?.split(' ').map(w=>w[0]).join('')}</div>
                <div style={{flex:1}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
                    <div>
                      <div style={{fontWeight:700}}>{b.tutor_name}</div>
                      <div style={{fontSize:'0.82rem',color:'var(--ink-400)'}}>{b.subject_name} - {b.level_name}</div>
                      <div style={{fontSize:'0.78rem',color:'var(--ink-400)',marginTop:2}}>{b.sessions_completed}/{b.total_sessions} sessions - {sym}{parseFloat(b.lesson_fee).toFixed(2)}/session</div>
                    </div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span className={'badge ' + (statusColor[b.status]||'badge-grey')}>{b.status}</span>
                      {b.status==='confirmed' && (
                        <button className="btn btn-danger btn-sm" onClick={()=>{
                          if(window.confirm('Cancel this booking? No refund will be issued.'))
                            api.post('/bookings/'+b.id+'/cancel',{reason:'Cancelled by parent'}).then(()=>window.location.reload());
                        }}>Cancel</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      }
    </DashboardLayout>
  );
}

export function ParentMessages() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [warning, setWarning] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(()=>{ api.get('/bookings/my').then(r=>setBookings((r.data.bookings||[]).filter(b=>b.status!=='cancelled'))); },[]);
  useEffect(()=>{
    if (!selected) return;
    const load = () => api.get('/messages/'+selected.id).then(r=>setMessages(r.data.messages||[]));
    load(); const iv=setInterval(load,5000); return()=>clearInterval(iv);
  },[selected]);
  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:'smooth'}); },[messages]);

  const send = async () => {
    if (!text.trim()||!selected) return;
    setSending(true); setWarning('');
    try {
      const r = await api.post('/messages/'+selected.id,{content:text});
      setMessages(m=>[...m,r.data.message]); setText('');
    } catch(err) { setWarning(err.response?.data?.message||'Failed'); }
    setSending(false);
  };

  return (
    <DashboardLayout title="Messages">
      <div style={{display:'grid',gridTemplateColumns:'280px 1fr',gap:0,height:'calc(100vh - 180px)',border:'1.5px solid var(--ink-100)',borderRadius:12,overflow:'hidden',background:'#fff'}}>
        <div style={{borderRight:'1.5px solid var(--ink-100)',overflowY:'auto'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid var(--ink-100)',fontWeight:700,fontSize:'0.82rem',color:'var(--ink-400)',textTransform:'uppercase'}}>Conversations</div>
          {bookings.length===0 && <p style={{padding:16,fontSize:'0.875rem',color:'var(--ink-400)'}}>No active bookings.</p>}
          {bookings.map(b=>(
            <div key={b.id} onClick={()=>setSelected(b)} style={{padding:'14px 16px',cursor:'pointer',borderBottom:'1px solid var(--ink-50)',background:selected?.id===b.id?'var(--green-50)':'transparent'}}>
              <div style={{fontWeight:700,fontSize:'0.875rem'}}>{b.tutor_name}</div>
              <div style={{fontSize:'0.75rem',color:'var(--ink-400)'}}>{b.subject_name}</div>
            </div>
          ))}
        </div>
        {!selected
          ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',color:'var(--ink-400)',flexDirection:'column',gap:8}}>
              <div style={{fontSize:'2.5rem'}}>💬</div><p style={{margin:0}}>Select a booking to message your tutor</p>
            </div>
          : <div style={{display:'flex',flexDirection:'column'}}>
              <div style={{padding:'12px 16px',borderBottom:'1px solid var(--ink-100)',fontWeight:700}}>{selected.tutor_name} - {selected.subject_name}</div>
              <div style={{flex:1,overflowY:'auto',padding:16,display:'flex',flexDirection:'column',gap:10}}>
                {messages.map((m,i)=>{
                  const mine = m.sender_id===user?.id;
                  return (
                    <div key={i} style={{display:'flex',justifyContent:mine?'flex-end':'flex-start'}}>
                      <div style={{maxWidth:'72%',padding:'10px 14px',borderRadius:mine?'16px 16px 4px 16px':'16px 16px 16px 4px',background:mine?'#22c97f':'var(--ink-50)',color:mine?'#fff':'var(--ink)',fontSize:'0.875rem'}}>
                        <p style={{margin:0}}>{m.content}</p>
                        <div style={{fontSize:'0.68rem',color:mine?'rgba(255,255,255,0.6)':'var(--ink-300)',marginTop:4,textAlign:mine?'right':'left'}}>{format(new Date(m.created_at),'HH:mm')}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef}/>
              </div>
              {warning && <div className="alert alert-warning" style={{margin:'0 12px',fontSize:'0.8rem'}}>{warning}</div>}
              <div style={{padding:12,borderTop:'1px solid var(--ink-100)',display:'flex',gap:8}}>
                <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&send()} placeholder="Type a message..." className="form-input" style={{flex:1}}/>
                <button className="btn btn-primary btn-sm" onClick={send} disabled={sending||!text.trim()}>Send</button>
              </div>
            </div>
        }
      </div>
    </DashboardLayout>
  );
}

export function ParentSettings() {
  const { user } = useAuth();
  return (
    <DashboardLayout title="Settings">
      <div style={{maxWidth:520}}>
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Account details</h3>
          <p style={{fontSize:'0.875rem',color:'var(--ink-500)',margin:0}}>
            <strong>Name:</strong> {user?.first_name} {user?.last_name}<br/>
            <strong>Email:</strong> {user?.email}<br/>
            <strong>Country:</strong> {user?.country==='GB'?'United Kingdom':'United States'}<br/>
            <strong>Currency:</strong> {user?.currency}
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ParentDashboard;
